import { useState, useEffect, useRef, useMemo } from "react";
import "./AllRepair.css";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faMagnifyingGlass, faTimes } from "@fortawesome/free-solid-svg-icons";
import CalendarIcon from "../../assets/icons/CalendarBlank.svg";
import Calendar from "react-calendar";
import {
  Link,
  useLocation,
  useNavigate,
  useSearchParams,
} from "react-router-dom";
import ProfileLayout from "../../components/profilelayout/ProfileLayout";
import ApiService from "../../components/Api_services";
import { useSelector } from "react-redux";
import { hasAccess } from "../../utils/Permission";
import { ACCESS_KEYS } from "../../constants.js";
import CustomDropdown from "../../components/customdropdown/CustomDropdown";
import FallbackImg from "../../components/FallbackImg.js";
import NewRequest from "../../components/newrequest/NewRequest.js";

function getPagination(current, total) {
  const pages = [];
  if (total <= 7) {
    for (let i = 1; i <= total; i++) pages.push(i);
  } else {
    pages.push(1);
    if (current > 4) pages.push("...");
    let start, end;
    if (current <= 4) {
      start = 2;
      end = 5;
    } else if (current >= total - 3) {
      start = total - 4;
      end = total - 1;
    } else {
      start = current - 1;
      end = current + 1;
    }
    for (let i = start; i <= end; i++) {
      if (i > 1 && i < total) pages.push(i);
    }
    if (current < total - 3) pages.push("...");
    pages.push(total);
  }
  return pages;
}

const AllRepair = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  // Derive page and tab from URL
  const page =
    parseInt(searchParams.get("page"), 10) > 0
      ? parseInt(searchParams.get("page"), 10)
      : 1;
  const activeTab = searchParams.get("status") || "all";

  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState(""); // This triggers API
  // const [page, setCurrentPage] = useState(1);
  // const [activeTab, setActiveTab] = useState("all");
  const [repairList, setRepairList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [totalRepairs, setTotalRepairs] = useState(0);
  const repairsPerPage = 5;
  // const [isSearching, setIsSearching] = useState(false);
  const [calendarOpen, setCalendarOpen] = useState(false);
  const [dateRange, setDateRange] = useState([null, null]); // [start, end]
  const [calendarTempRange, setCalendarTempRange] = useState([null, null]);
  const inputRef = useRef(null);
  const [showNewRequest, setShowNewRequest] = useState(false);
  const [statusCounts, setStatusCounts] = useState({
    all: 0,
    new: 0,
    collected: 0,
    repairing: 0,
    shipping: 0,
    received: 0,
    refund: 0,
    cancelled: 0,
  });

  const statusTypeMap = {
    all: "",
    new: "new",
    collected: "collected",
    repairing: "repairing",
    shipping: "to_ship",
    received: "to_receive",
    refund: "refund",
    cancelled: "cancelled",
  };

  const [allBranchOptions, setAllBranchOptions] = useState([
    { value: "", label: "All Branch" },
  ]);

  const [selectedBranch, setSelectedBranch] = useState({
    value: "",
    label: "All Branch",
  });

  const sub_role = useSelector((state) => state.auth.sub_role) || [];
  const is_subuser =
    useSelector((state) => state.auth.user?.is_subuser) || false;
  const canViewRepair =
    !is_subuser || hasAccess(sub_role, ACCESS_KEYS.MY_REPAIR);
  const canAddNewRequest =
    !is_subuser || hasAccess(sub_role, ACCESS_KEYS.MY_REPAIR);

  const formatDate = (dateStr) => {
    if (!dateStr) return "";
    return new Date(dateStr).toLocaleString("en-MY", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  };

  const fetchStatusCounts = async () => {
    try {
      const response = await ApiService.getRepairStatusCounts();

      if (response && response.status_counts) {
        // Calculate total count
        const counts = response.status_counts;
        const total = Object.values(counts).reduce(
          (sum, count) => sum + count,
          0,
        );

        setStatusCounts({
          all: total,
          ...counts,
        });
      }
    } catch (error) {
      console.error("Error fetching repair status counts:", error);
    }
  };

  // useEffect(() => {
  //   const queryParams = new URLSearchParams(location.search);
  //   const status = queryParams.get("status");
  //   if (status) {
  //     setActiveTab(status);
  //   } else {
  //     setActiveTab("all");
  //   }
  // }, [location.search]);

  // const handleTabChange = (tab) => {
  //   navigate(`/allrepair${tab === "all" ? "" : `?status=${tab}`}`);
  // };

  // Update URL when tab changes (reset to page 1)
  const handleTabChange = (tab) => {
    navigate(`/allrepair?status=${tab}&page=1`);
  };

  // const handlePageChange = (newPage) => {
  //   setCurrentPage(newPage);
  //   fetchRepairs(newPage, search);
  //   window.scrollTo(0, 0);
  // };

  // Update URL when page changes
  const handlePageChange = (newPage) => {
    navigate(`/allrepair?status=${activeTab}&page=${newPage}`);
  };

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, [page]);

  // Add this effect to sync defaults to URL
  useEffect(() => {
    const params = Object.fromEntries([...searchParams]);
    let changed = false;
    if (!params.status) {
      params.status = "all";
      changed = true;
    }
    if (!params.page) {
      params.page = 1;
      changed = true;
    }
    if (changed) {
      setSearchParams(params);
    }
    // eslint-disable-next-line
  }, []);

  // Fetch repairs based on current filters
  const fetchRepairs = async (
    pageNum = 1,
    searchText = search,
    branch = selectedBranch,
    range = dateRange,
  ) => {
    setLoading(true);
    setError(null);
    try {
      const params = {
        page: pageNum,
        status_type: statusTypeMap[activeTab] || "",
        limit: repairsPerPage,
      };

      if (searchText) {
        params.search = searchText;
      }

      if (branch.value) {
        params.branch_id = branch.value;
      }

      // Add date range if both dates are selected
      if (range && range[0] && range[1]) {
        const pad = (n) => n.toString().padStart(2, "0");
        const formatLocalDate = (d) =>
          `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
        params.date_from = formatLocalDate(range[0]);
        params.date_to = formatLocalDate(range[1]);
      }

      // console.log("API params:", params);

      const response = await ApiService.getRepairList(params);

      if (response && response.data) {
        setRepairList(response.data);
        setTotalRepairs(response.pagination?.total_records || 0);
      } else {
        setRepairList([]);
        setTotalRepairs(0);
        setError("No repair data found.");
      }
    } catch (error) {
      console.error("Error fetching repairs:", error);
      setError("An error occurred while fetching repairs.");
      //setRepairList([]);
    } finally {
      setLoading(false);
      // setIsSearching(false);
    }
  };

  // Fetch repairs when tab or page changes
  useEffect(() => {
    fetchRepairs(page, search, selectedBranch, dateRange);
  }, [activeTab, page, search, selectedBranch, dateRange]);

  useEffect(() => {
    // Fetch status counts when component mounts
    fetchStatusCounts();
  }, []);

  const fetchBranches = async () => {
    try {
      let data;
      data = await ApiService.getBranchList();
      if (Array.isArray(data)) {
        const options = data.map((item) => ({
          value: item.branch_id,
          label: item.branch_code,
        }));
        setAllBranchOptions([{ value: "", label: "All Branch" }, ...options]);
      }
    } catch (err) {
      console.error("Error fetching branch list:", err);
    }
  };

  useEffect(() => {
    fetchBranches();
  }, []);

  const handleSearch = (e) => {
    e.preventDefault();
    setSearch(searchInput);
    // Update URL to page 1
    const params = new URLSearchParams(location.search);
    params.set("page", 1);
    navigate(`${location.pathname}?${params.toString()}`, { replace: true });
  };

  const clearSearch = () => {
    setSearchInput("");
    setSearch("");
    navigate(`/allrepair?status=${activeTab}&page=1`);
    fetchRepairs(1, "");
  };

  useEffect(() => {
    if (searchInput === "") {
      setSearch("");
    }
  }, [searchInput]);

  // Get display text for repair status
  const getStatusText = (status) => {
    switch (status) {
      case "new":
        return "Reviewing your request";
      case "collected":
        return "Item collected";
      case "repairing":
        return "Item is being repaired";
      case "shipping":
        return "Item is being shipped";
      case "received":
        return "Repair completed";
      case "refund":
        return "Refund processing";
      case "cancelled":
        return "Request cancelled";
      default:
        return "Processing";
    }
  };

  const tabScrollRef = useRef(null);

  const scrollTabs = (direction) => {
    if (tabScrollRef.current) {
      const scrollAmount = 120; // px per click
      tabScrollRef.current.scrollBy({
        left: direction === "left" ? -scrollAmount : scrollAmount,
        behavior: "smooth",
      });
    }
  };

  // Handler for range selection
  const handleDateRangeChange = (range) => {
    setDateRange(range);
    setCalendarOpen(false);

    // Reset page to 1 in the URL
    const params = new URLSearchParams(location.search);
    params.set("page", 1);
    navigate(`${location.pathname}?${params.toString()}`, { replace: true });
  };

  const handleBranchChange = (selectedOption) => {
    setSelectedBranch(selectedOption);
    // Update URL to page 1
    const params = new URLSearchParams(location.search);
    params.set("page", 1);
    navigate(`${location.pathname}?${params.toString()}`, { replace: true });
  };

  // if (!canViewRepair) {
  //   return (
  //     <div
  //       style={{
  //         minHeight: "60vh",
  //         display: "flex",
  //         alignItems: "center",
  //         justifyContent: "center",
  //       }}
  //     >
  //       <span style={{ color: "#b71c1c", fontWeight: 600 }}>
  //         You do not have permission to view repairs.
  //       </span>
  //     </div>
  //   );
  // }

  return (
    <ProfileLayout>
      <div className="allrepair">
        <div className="allrepair-content">
          <div className="myrepair-title">
            <strong>MY REPAIR</strong>
          </div>

          {canViewRepair ? (
            <>
              <div className="tab-scroll-wrapper">
                <button
                  className="tab-scroll-btn left"
                  onClick={() => scrollTabs("left")}
                  type="button"
                >
                  &lt;
                </button>
                <div className="tab-scroll" ref={tabScrollRef}>
                  <div className="tab">
                    <div
                      className={`rp-alltab${
                        activeTab === "all" ? " active-tab" : ""
                      }`}
                      onClick={() => {
                        if (canViewRepair) handleTabChange("all");
                      }}
                      style={{
                        opacity: canViewRepair ? 1 : 0.5,
                        cursor: canViewRepair ? "pointer" : "not-allowed",
                      }}
                      title={canViewRepair ? "" : "No permission"}
                    >
                      All
                    </div>
                    {statusCounts.all > 0 && canViewRepair && (
                      <span className="tab-count">({statusCounts.all})</span>
                    )}
                    <div
                      className={`rp-newtab${
                        activeTab === "new" ? " active-tab" : ""
                      }`}
                      onClick={() => {
                        if (canViewRepair) handleTabChange("new");
                      }}
                      style={{
                        opacity: canViewRepair ? 1 : 0.5,
                        cursor: canViewRepair ? "pointer" : "not-allowed",
                      }}
                      title={canViewRepair ? "" : "No permission"}
                    >
                      New
                    </div>
                    {statusCounts.new > 0 && canViewRepair && (
                      <span className="tab-count">({statusCounts.new})</span>
                    )}
                    <div
                      className={`rp-collectedtab${
                        activeTab === "collected" ? " active-tab" : ""
                      }`}
                      onClick={() => {
                        if (canViewRepair) handleTabChange("collected");
                      }}
                      style={{
                        opacity: canViewRepair ? 1 : 0.5,
                        cursor: canViewRepair ? "pointer" : "not-allowed",
                      }}
                      title={canViewRepair ? "" : "No permission"}
                    >
                      Collected
                    </div>
                    {statusCounts.collected > 0 && canViewRepair && (
                      <span className="tab-count">
                        ({statusCounts.collected})
                      </span>
                    )}
                    <div
                      className={`rp-repairingtab${
                        activeTab === "repairing" ? " active-tab" : ""
                      }`}
                      onClick={() => {
                        if (canViewRepair) handleTabChange("repairing");
                      }}
                      style={{
                        opacity: canViewRepair ? 1 : 0.5,
                        cursor: canViewRepair ? "pointer" : "not-allowed",
                      }}
                      title={canViewRepair ? "" : "No permission"}
                    >
                      Repairing
                    </div>
                    {statusCounts.repairing > 0 && canViewRepair && (
                      <span className="tab-count">
                        ({statusCounts.repairing})
                      </span>
                    )}
                    <div
                      className={`rp-shippingtab${
                        activeTab === "shipping" ? " active-tab" : ""
                      }`}
                      onClick={() => {
                        if (canViewRepair) handleTabChange("shipping");
                      }}
                      style={{
                        opacity: canViewRepair ? 1 : 0.5,
                        cursor: canViewRepair ? "pointer" : "not-allowed",
                      }}
                      title={canViewRepair ? "" : "No permission"}
                    >
                      Shipping
                    </div>
                    {statusCounts.shipping > 0 && canViewRepair && (
                      <span className="tab-count">
                        ({statusCounts.shipping})
                      </span>
                    )}
                    <div
                      className={`rp-receivedtab${
                        activeTab === "received" ? " active-tab" : ""
                      }`}
                      onClick={() => {
                        if (canViewRepair) handleTabChange("received");
                      }}
                      style={{
                        opacity: canViewRepair ? 1 : 0.5,
                        cursor: canViewRepair ? "pointer" : "not-allowed",
                      }}
                      title={canViewRepair ? "" : "No permission"}
                    >
                      Received
                    </div>
                    {/* {statusCounts.received > 0 && canViewRepair && (
                  <span className="tab-count">({statusCounts.received})</span>
                )} */}
                    <div
                      className={`rp-refundtab${
                        activeTab === "refund" ? " active-tab" : ""
                      }`}
                      onClick={() => {
                        if (canViewRepair) handleTabChange("refund");
                      }}
                      style={{
                        opacity: canViewRepair ? 1 : 0.5,
                        cursor: canViewRepair ? "pointer" : "not-allowed",
                      }}
                      title={canViewRepair ? "" : "No permission"}
                    >
                      Refund
                    </div>
                    {/* {statusCounts.refund > 0 && canViewRepair && (
                  <span className="tab-count">({statusCounts.refund})</span>
                )} */}
                    <div
                      className={`rp-cancelledtab${
                        activeTab === "cancelled" ? " active-tab" : ""
                      }`}
                      onClick={() => {
                        if (canViewRepair) handleTabChange("cancelled");
                      }}
                      style={{
                        opacity: canViewRepair ? 1 : 0.5,
                        cursor: canViewRepair ? "pointer" : "not-allowed",
                      }}
                      title={canViewRepair ? "" : "No permission"}
                    >
                      Cancelled
                    </div>
                    {/* {statusCounts.cancelled > 0 && canViewRepair && (
                  <span className="tab-count">({statusCounts.cancelled})</span>
                )} */}
                  </div>
                </div>
                <button
                  className="tab-scroll-btn right"
                  onClick={() => scrollTabs("right")}
                  type="button"
                >
                  &gt;
                </button>
              </div>
              <div className="allrepair-line"></div>

              <div className="allrepair-main">
                <form onSubmit={handleSearch} className="searchbar-allrpr">
                  <FontAwesomeIcon
                    icon={faMagnifyingGlass}
                    style={{
                      color: "#798777",
                      fontSize: "18px",
                    }}
                  />
                  <input
                    placeholder="Search by Job ID, Item Code, Item Name"
                    value={searchInput}
                    onChange={(e) => setSearchInput(e.target.value)}
                  />
                  {searchInput && (
                    <FontAwesomeIcon
                      icon={faTimes}
                      onClick={clearSearch}
                      style={{
                        color: "#798777",
                        fontSize: "16px",
                        cursor: "pointer",
                        marginRight: "10px",
                      }}
                    />
                  )}
                </form>

                {/* Date Picker */}
                <form
                  className="ar-date-picker-container"
                  onSubmit={(e) => {
                    e.preventDefault();
                    if (calendarTempRange?.[0] && calendarTempRange?.[1]) {
                      handleDateRangeChange(calendarTempRange);
                    }
                  }}
                >
                  <input
                    ref={inputRef}
                    type="text"
                    className="ar-date-range-input"
                    value={
                      calendarTempRange?.[0]
                        ? `${new Date(calendarTempRange[0]).toLocaleDateString(
                            "en-MY",
                            {
                              day: "2-digit",
                              month: "short",
                              year: "numeric",
                            },
                          )} - ${
                            calendarTempRange[1]
                              ? new Date(
                                  calendarTempRange[1],
                                ).toLocaleDateString("en-MY", {
                                  day: "2-digit",
                                  month: "short",
                                  year: "numeric",
                                })
                              : "End Date"
                          }`
                        : "Select Date Range"
                    }
                    onClick={() => setCalendarOpen((open) => !open)}
                    readOnly
                  />
                  {(calendarTempRange?.[0] || calendarTempRange?.[1]) && (
                    <FontAwesomeIcon
                      icon={faTimes}
                      style={{
                        marginLeft: 8,
                        color: "#aaa",
                        cursor: "pointer",
                      }}
                      onClick={(e) => {
                        e.stopPropagation();
                        setCalendarTempRange([null, null]);
                        setDateRange([null, null]);
                        setCalendarOpen(false);
                      }}
                    />
                  )}
                  <FallbackImg
                    src={CalendarIcon}
                    alt="calendar"
                    width="22"
                    height="22"
                    style={{ cursor: "pointer" }}
                    onClick={() => setCalendarOpen((open) => !open)}
                  />
                  {calendarOpen && (
                    <div className="allrepair-calendar">
                      <Calendar
                        selectRange
                        onChange={(range) => {
                          setCalendarTempRange(range);
                          // Focus input after two dates are picked
                          if (range?.[0] && range?.[1] && inputRef.current) {
                            inputRef.current.focus();
                          }
                          setCalendarOpen(false);
                        }}
                        value={calendarTempRange}
                        maxDate={new Date()}
                      />
                    </div>
                  )}
                  {/* Hidden submit button to allow Enter key to trigger onSubmit */}
                  <button
                    type="submit"
                    style={{ display: "none" }}
                    tabIndex={-1}
                  ></button>
                </form>

                <CustomDropdown
                  options={allBranchOptions}
                  value={selectedBranch.value}
                  onChange={(val, opt) => handleBranchChange(opt)}
                  placeholder="All Branch"
                  className="allrepair-dropdown"
                />

                <div
                  style={{
                    display: "inline-block",
                    cursor: canAddNewRequest ? "pointer" : "not-allowed",
                  }}
                  title={canAddNewRequest ? "" : "No permission"}
                  onClick={(e) => {
                    if (!canAddNewRequest) {
                      e.preventDefault();
                      return;
                    }
                    setShowNewRequest(true);
                  }}
                >
                  <button
                    className="newrequest-button"
                    disabled={!canAddNewRequest}
                    style={{
                      opacity: canAddNewRequest ? 1 : 0.5,
                      pointerEvents: canAddNewRequest ? "auto" : "none",
                    }}
                  >
                    NEW REQUEST
                  </button>
                </div>
              </div>

              <div className="allrepair-sub">
                {repairList.length === 0 && loading ? (
                  <p>Loading...</p>
                ) : error ? (
                  <p style={{ color: "red" }}>{error}</p>
                ) : repairList.length === 0 ? (
                  <p>No repair requests found.</p>
                ) : (
                  repairList.map((repair, index) => (
                    <Link
                      to={`/repairdetail/${repair.request_id}?status=${activeTab}`}
                      key={index}
                    >
                      <div className="allrepair-item">
                        <div
                          style={{ width: 25, display: "inline-block" }}
                        ></div>

                        <div className="aritem-leftcenter-container">
                          <div className="aritem-tag-main">
                            <div className={`aritem-tag ${repair.status}`}>
                              {repair.status.charAt(0).toUpperCase() +
                                repair.status.slice(1)}
                            </div>
                          </div>

                          <div style={{ display: "flex" }}>
                            <div className="aritem-left">
                              <div className="aritem-id">
                                <span className="aritem-subtitle">Job ID</span>
                                <div className="aritem-colon">:</div>
                                <div className="aritem-value">
                                  {repair.request_id ? repair.request_id : "-"}
                                </div>
                              </div>
                              <div className="aritem-date">
                                <span className="aritem-subtitle">
                                  Submit On
                                </span>
                                <div className="aritem-colon">:</div>
                                <div className="aritem-value">
                                  {repair.request_date
                                    ? formatDate(repair.request_date)
                                    : "-"}
                                </div>
                              </div>
                            </div>
                            <div className="aritem-center">
                              <div className="aritem-name">
                                <span className="aritem-subtitle">
                                  Item Code
                                </span>
                                <div className="aritem-colon">:</div>
                                <div className="aritem-value">
                                  {repair.item_code ? repair.item_code : "-"}
                                </div>
                              </div>
                              {/* <div className="aritem-process">
                                {getStatusText(repair.status)}
                              </div> */}
                              <div className="aritem-delivery">
                                <span className="aritem-subtitle">Branch</span>
                                <div className="aritem-colon">:</div>
                                <div className="aritem-value">
                                  {repair.branch_code
                                    ? repair.branch_code
                                    : "-"}
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>

                        <div className="aritem-right">
                          <FallbackImg
                            src={repair.item_photo}
                            alt={repair.item_code}
                            style={{
                              width: "100px",
                              height: "90px",
                              objectFit: "contain",
                              borderRadius: "5px",
                            }}
                          />
                        </div>

                        <div className="ar-nextsign">&gt;</div>
                      </div>
                    </Link>
                  ))
                )}
              </div>

              {repairList.length > 0 && (
                <div className="pagination-modern">
                  <button
                    className="pagination-btn"
                    onClick={() => handlePageChange(page - 1)}
                    disabled={page === 1}
                  >
                    <span className="arrow">&lt;</span>
                    <span className="prevnext-text">Prev</span>
                  </button>
                  {getPagination(
                    page,
                    Math.ceil(totalRepairs / repairsPerPage),
                  ).map((p, idx) =>
                    p === "..." ? (
                      <span
                        key={`ellipsis-${idx}`}
                        className="pagination-ellipsis"
                      >
                        ...
                      </span>
                    ) : (
                      <button
                        key={`page-${p}-${idx}`}
                        className={`pagination-btn${
                          page === p ? " pagination-current" : ""
                        }`}
                        onClick={() => handlePageChange(p)}
                        disabled={page === p}
                      >
                        {p}
                      </button>
                    ),
                  )}
                  <button
                    className="pagination-btn"
                    onClick={() => handlePageChange(page + 1)}
                    disabled={page === Math.ceil(totalRepairs / repairsPerPage)}
                  >
                    <span className="prevnext-text">Next</span>
                    <span className="arrow">&gt;</span>
                  </button>
                </div>
              )}
            </>
          ) : (
            <div
              style={{
                minHeight: "60vh",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <span style={{ color: "#b71c1c", fontWeight: 600 }}>
                You do not have permission to view My Repair.
              </span>
            </div>
          )}

          {showNewRequest && (
            <NewRequest onClose={() => setShowNewRequest(false)} />
          )}
        </div>
      </div>
    </ProfileLayout>
  );
};

export default AllRepair;
